// Monitoring dashboard — 4 daily time-series charts sourced from
// `adminTimeSeries` GraphQL query. Native DB-backed (no external metrics
// infra); accuracy is one-day granularity for the past 30 days.

import { useEffect, useState } from "react";
import { gql } from "../lib/gql";

type TSPoint = { date: string; value: number };
type TSData = {
  partners_created: TSPoint[];
  points_created: TSPoint[];
  devices_created: TSPoint[];
  activity_total: TSPoint[];
};

const QUERY = `
  query Monitoring($days: Int) {
    partners_created: adminTimeSeries(metric: "partners_created", days: $days) { date value }
    points_created:   adminTimeSeries(metric: "points_created",   days: $days) { date value }
    devices_created:  adminTimeSeries(metric: "devices_created",  days: $days) { date value }
    activity_total:   adminTimeSeries(metric: "activity_total",   days: $days) { date value }
  }
`;

const DEFAULT_DAYS = 30;

export function MonitoringPage() {
  const [data, setData] = useState<TSData | null>(null);
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await gql<TSData>(QUERY, { days });
        setData(d);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [days]);

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Мониторинг</h2>
        <div className="row">
          <span className="muted" style={{ fontSize: 12 }}>Период:</span>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{
              background: "var(--bg-elevated)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              padding: "4px 10px",
              borderRadius: 4,
            }}
          >
            <option value={7}>7 дней</option>
            <option value={14}>14 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
          </select>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <ChartCard title="Новые партнёры / день" color="#c49a4a" points={data.partners_created} />
          <ChartCard title="Новые точки / день" color="#5ac48a" points={data.points_created} />
          <ChartCard title="Новые устройства / день" color="#5a8ac4" points={data.devices_created} />
          <ChartCard title="Активность (все события)" color="#c45a8a" points={data.activity_total} />
        </div>
      )}
    </div>
  );
}

// Minimal SVG chart — avoids pulling in chart.js / recharts (~200kb) for
// what is literally 4 line charts on an internal tool. 600x180 is enough
// resolution to see weekly patterns. Labels show first/last date + peak.
function ChartCard({ title, color, points }: { title: string; color: string; points: TSPoint[] }) {
  if (points.length === 0) {
    return (
      <div className="card">
        <h3 style={{ margin: "0 0 8px 0", fontSize: 14 }}>{title}</h3>
        <div className="muted">Нет данных.</div>
      </div>
    );
  }

  const W = 600;
  const H = 180;
  const pad = { top: 16, right: 12, bottom: 24, left: 32 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const values = points.map((p) => p.value);
  const maxV = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const peak = Math.max(...values);

  const xAt = (i: number) =>
    pad.left + (points.length === 1 ? innerW / 2 : (i * innerW) / (points.length - 1));
  const yAt = (v: number) => pad.top + innerH - (v / maxV) * innerH;

  // Line path
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`)
    .join(" ");

  // Area under line for nicer visual — use color with opacity.
  const areaPath =
    path +
    ` L ${xAt(points.length - 1).toFixed(1)} ${(pad.top + innerH).toFixed(1)}` +
    ` L ${xAt(0).toFixed(1)} ${(pad.top + innerH).toFixed(1)} Z`;

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>
        <div className="muted" style={{ fontSize: 11 }}>
          Σ {total} · max {peak}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Axes */}
        <line
          x1={pad.left}
          y1={pad.top + innerH}
          x2={pad.left + innerW}
          y2={pad.top + innerH}
          stroke="var(--border)"
          strokeWidth={1}
        />
        {/* Y-axis label: max value */}
        <text
          x={pad.left - 6}
          y={pad.top + 4}
          fill="var(--text-dim)"
          fontSize={10}
          textAnchor="end"
        >
          {maxV}
        </text>
        <text
          x={pad.left - 6}
          y={pad.top + innerH + 2}
          fill="var(--text-dim)"
          fontSize={10}
          textAnchor="end"
        >
          0
        </text>
        {/* Area + line */}
        <path d={areaPath} fill={color} opacity={0.15} />
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {/* Dots on peak only (less visual noise) */}
        {points.map(
          (p, i) =>
            p.value === peak && (
              <circle
                key={i}
                cx={xAt(i)}
                cy={yAt(p.value)}
                r={3}
                fill={color}
                stroke="var(--bg-card)"
                strokeWidth={1}
              />
            )
        )}
        {/* X-axis: first / mid / last date */}
        <text
          x={pad.left}
          y={H - 6}
          fill="var(--text-dim)"
          fontSize={10}
          textAnchor="start"
        >
          {shortDate(points[0].date)}
        </text>
        <text x={W / 2} y={H - 6} fill="var(--text-dim)" fontSize={10} textAnchor="middle">
          {shortDate(points[Math.floor(points.length / 2)].date)}
        </text>
        <text
          x={pad.left + innerW}
          y={H - 6}
          fill="var(--text-dim)"
          fontSize={10}
          textAnchor="end"
        >
          {shortDate(points[points.length - 1].date)}
        </text>
      </svg>
    </div>
  );
}

function shortDate(d: string): string {
  // YYYY-MM-DD → DD.MM
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
  return d;
}
